import { api, API_BASE } from "@/lib/apiClient";
import type { KalkStundenAppEntry, Order, TagOut } from "@/lib/projects";

export interface AppAngebotLohnZeile {
  lohnart_id?: string | null;
  bezeichnung?: string | null;
  stunden: number;
  stundensatz_netto?: number;
  /** gesamt = Stunden gelten einmal für die Positionsmenge. */
  preisbezug?: "einzel" | "gesamt" | null;
}

export interface AppAngebotPosition {
  id?: number | null;
  position_nr?: number | null;
  sort_index?: number | null;
  position_typ: string;
  bezeichnung: string;
  beschreibung?: string | null;
  einheit?: string | null;
  menge: number;
  preisart?: "festpreis" | "nachweis" | null;
  lohn_liste?: AppAngebotLohnZeile[];
  stunden?: number | null;
  /** Angebotene Nachweis-Stunden dieser Position. */
  nachweis_stunden?: number | null;
  einzelpreis_netto?: number;
  rabatt_prozent?: number;
  steuersatz_prozent?: number;
  gesamt_netto?: number;
}

export interface AppAngebotBundle {
  dokument_id: number | null;
  dokument_nummer?: string | null;
  status?: string | null;
  betreff?: string | null;
  einleitung?: string | null;
  schlusstext?: string | null;
  belegdatum?: string | null;
  beauftragt_am?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at?: string | null;
  ist_beauftragt?: boolean;
  positionen: AppAngebotPosition[];
  /** Alle beauftragten (bzw. Fallback-) App-Angebote; Stunden oben sind aggregiert. */
  angebote?: AppAngebotBundle[];
  kalk_stunden: KalkStundenAppEntry[];
  /** Summe angebotener Nachweis-Stunden. */
  nachweis_stunden?: number | null;
  include_prices: boolean;
  summe_netto?: number;
  summe_steuer?: number;
  summe_brutto?: number;
}

export function getAuftragAppAngebot(orderId: number): Promise<AppAngebotBundle> {
  return api.get<AppAngebotBundle>(
    `/auftraege/${encodeURIComponent(String(orderId))}/app-angebot`,
  );
}

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export type { Order };

export interface ProjectSchadensmeldung {
  id: number;
  auftrag_id: number;
  kind?: string | null;
  filename: string;
  sharepoint_web_url?: string | null;
  file_url?: string | null;
  montageunterlage_id?: number | null;
  created_by_login?: string | null;
  created_by_display?: string | null;
  created_at?: string | null;
}

export interface ProjectOverviewBundle {
  project: Order;
  tags: TagOut[];
  tagSuggestions: string[];
  timesheets: ProjectTimesheet[];
  schadensmeldungen: ProjectSchadensmeldung[];
  orders: OrderedMaterialLine[];
  notes: ProjectNote[];
}

export interface ProjectTimesheet {
  id: number;
  status?: string | null;
  status_display?: string | null;
  display_number?: string | null;
  timesheet_number?: number | string | null;
  base_timesheet_id?: number | null;
  revision_index?: number | null;
  has_newer_revision?: boolean;
  period_start?: string | null;
  period_end?: string | null;
  hours_total?: number | null;
  public_token?: string | null;
  [key: string]: unknown;
}

export interface OrderedMaterialLine {
  id?: number;
  menge?: number | string | null;
  default_menge?: number | string | null;
  einheit?: string | null;
  text?: string | null;
  label?: string | null;
  bestellnummer?: string | null;
  hero_nr?: string | null;
  lot_nummer?: string | null;
  has_werkszeugnis?: boolean;
  order_cancelled?: boolean;
  geliefert_at?: string | null;
  order_created_at?: string | null;
  supplier_name?: string | null;
  supplier_gp_id?: number | null;
  supplier_has_logo?: boolean | null;
  supplier_logo_updated_at?: string | null;
  supplier_contact_name?: string | null;
  kuerzel?: string | null;
  lieferart?: string | null;
  lieferadresse_abkuerzung?: string | null;
  order_id?: number;
  order_line_id?: number;
  used_timesheet_id?: number | null;
  [key: string]: unknown;
}

export interface AuftragHeroIds {
  hero_customer_id?: number | null;
  hero_contact_id?: number | null;
  hero_address_id?: number | null;
}

export function getAuftragHeroIds(orderId: number): Promise<AuftragHeroIds> {
  return api.get<AuftragHeroIds>(
    `/auftraege/${encodeURIComponent(String(orderId))}/hero-ids`,
    { cache: "no-store" },
  );
}

export interface ProjectNoteReference {
  type: string;
  ref_id?: string | number;
  id?: number | string;
  label?: string | null;
  trigger?: string | null;
  token_text?: string | null;
  start?: number | null;
  end?: number | null;
  position?: number | null;
}

export interface ProjectNote {
  id: number | string;
  auftrag_id?: number;
  note: string;
  created_by_login?: string | null;
  created_by_display?: string | null;
  created_at?: string | null;
  visibility?: "all" | "buero" | string;
  references?: ProjectNoteReference[] | null;
  _pending?: boolean;
}

export interface ProjectAssignments {
  auftrag_id?: number;
  hero_project_id?: number;
  assigned_users?: Array<{
    hero_user_id?: number;
    hero_partner_id?: number;
    name?: string | null;
    email?: string | null;
  }>;
}

export interface HeroDocument {
  id?: number | string | null;
  customer_document_id?: number | string | null;
  name?: string | null;
  title?: string | null;
  nr?: string | null;
  url?: string | null;
  download_url?: string | null;
  created_at?: string | null;
  value?: number | null;
  type?: string | null;
  document_type_name?: string | null;
  document_base_type?: string | null;
  status_code?: string | number | null;
  is_draft?: boolean | null;
  status_name?: string | null;
  booking_status_name?: string | null;
  booking_modified?: string | null;
  booking_is_open?: boolean | null;
  invoice_style?: string | null;
  billing_art_override?: string | null;
  effective_rechnung_art?: string | null;
  [key: string]: unknown;
}

export interface UpdateOrderPayload {
  ordner_id: number | null;
  sordner_id: number | null;
  nummer: number;
  ansprechpartner_id: number;
  hero_address_id: number | null;
  ort_id: number | null;
  ort_name_text?: string;
  ort_zusatz: string | null;
  beschreibung: string;
  jahr: number | null;
  hero_customer_id?: number | null;
  geschaeftspartner_id?: number | null;
  bestellnummer?: string | null;
  kundenreferenz?: string | null;
  leitweg_id?: string | null;
  zahlungsart?: "rechnung" | "vorkasse" | null;
  zahlungsziel_tage?: number | null;
  skonto_prozent?: number | null;
  skontoziel_tage?: number | null;
}

export interface AuftragErstellerKontakt {
  user_login: string | null;
  display_name: string | null;
  email: string | null;
  telefon: string | null;
  mobil: string | null;
  created_at: string | null;
}

export function getAuftragErsteller(orderId: number): Promise<AuftragErstellerKontakt> {
  return api.get<AuftragErstellerKontakt>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ersteller`,
    { cache: "no-store" },
  );
}

export function getOrder(orderId: number): Promise<Order> {
  return api.get<Order>(`/auftraege/${encodeURIComponent(String(orderId))}`, {
    cache: "no-store",
  });
}

export async function getOrderTags(orderId: number): Promise<{ tags: TagOut[]; suggestions: string[] }> {
  const data = await api.get<{ tags?: TagOut[]; suggestions?: Array<{ name: string }> }>(
    `/auftraege/${encodeURIComponent(String(orderId))}/tags`,
    { cache: "no-store" },
  );
  const tags = Array.isArray(data?.tags) ? data.tags : [];
  const suggestions = (data?.suggestions ?? [])
    .map((t) => String(t?.name || "").trim())
    .filter(Boolean);
  return { tags, suggestions };
}

export function getOrderTimesheets(orderId: number): Promise<ProjectTimesheet[]> {
  return api.get<ProjectTimesheet[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/timesheets`,
    { cache: "no-store" },
  );
}

export function getOrderSchadensmeldungen(orderId: number): Promise<ProjectSchadensmeldung[]> {
  return api.get<ProjectSchadensmeldung[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/schadensmeldungen`,
    { cache: "no-store" },
  );
}

export function getOrderedMaterials(orderId: number): Promise<OrderedMaterialLine[]> {
  return api.get<OrderedMaterialLine[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ordered-materials`,
    { cache: "no-store" },
  );
}

export function getOrderNotes(orderId: number): Promise<ProjectNote[]> {
  return api.get<ProjectNote[]>(`/auftraege/${encodeURIComponent(String(orderId))}/notes`, {
    cache: "no-store",
  });
}

export async function getAssignments(orderId: number): Promise<{
  data: ProjectAssignments | null;
  ok: boolean;
}> {
  try {
    const data = await api.get<ProjectAssignments>(
      `/auftraege/${encodeURIComponent(String(orderId))}/assignments`,
      { cache: "no-store" },
    );
    return { data, ok: true };
  } catch {
    return { data: null, ok: false };
  }
}

export function getHeroDocuments(orderId: number): Promise<HeroDocument[]> {
  return api.get<HeroDocument[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/hero-documents`,
    { cache: "no-store" },
  );
}

export async function loadProjectOverviewBundle(
  orderId: number,
  options?: { includeBueroSections?: boolean },
): Promise<ProjectOverviewBundle> {
  const includeBueroSections = options?.includeBueroSections !== false;
  const settled = await Promise.allSettled([
    getOrder(orderId),
    getOrderTags(orderId),
    getOrderTimesheets(orderId),
    includeBueroSections ? getOrderedMaterials(orderId) : Promise.resolve([]),
    getOrderNotes(orderId),
    getOrderSchadensmeldungen(orderId),
  ]);

  const project =
    settled[0].status === "fulfilled" ? settled[0].value : null;
  if (!project) {
    throw new Error("Projekt konnte nicht geladen werden.");
  }

  const tagsResult = settled[1].status === "fulfilled" ? settled[1].value : { tags: [], suggestions: [] };
  const timesheets = settled[2].status === "fulfilled" ? settled[2].value : [];
  const orders = settled[3].status === "fulfilled" ? settled[3].value : [];
  const notes = settled[4].status === "fulfilled" ? settled[4].value : [];
  const schadensmeldungen = settled[5].status === "fulfilled" ? settled[5].value : [];

  return {
    project,
    tags: tagsResult.tags,
    tagSuggestions: tagsResult.suggestions,
    timesheets: Array.isArray(timesheets) ? timesheets : [],
    schadensmeldungen: Array.isArray(schadensmeldungen) ? schadensmeldungen : [],
    orders: Array.isArray(orders) ? orders : [],
    notes: Array.isArray(notes) ? notes : [],
  };
}

export function setOrderTags(orderId: number, tags: string[]): Promise<unknown> {
  return api.put(`/auftraege/${encodeURIComponent(String(orderId))}/tags`, { tags });
}

export interface AuftragLinkedAsset {
  link_id: number;
  asset_id: number;
  external_id: string;
  name?: string | null;
  status?: string | null;
  maintenance_type_id: number;
  maintenance_type_key: string;
  maintenance_type_name?: string | null;
  hero_address_id?: number | null;
  sub_location_id?: number | null;
  masterdata?: Record<string, unknown>;
  created_at?: string | null;
  created_by?: string | null;
}

export interface AuftragLinkedAssetsResponse {
  assets: AuftragLinkedAsset[];
  hero_address_id?: number | null;
}

export function getOrderLinkedAssets(orderId: number): Promise<AuftragLinkedAssetsResponse> {
  return api.get<AuftragLinkedAssetsResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/assets`,
    { cache: "no-store" },
  );
}

export function addOrderLinkedAssets(
  orderId: number,
  assetIds: number[],
): Promise<AuftragLinkedAssetsResponse> {
  return api.post<AuftragLinkedAssetsResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/assets`,
    { asset_ids: assetIds },
  );
}

export function removeOrderLinkedAsset(
  orderId: number,
  assetId: number,
): Promise<AuftragLinkedAssetsResponse> {
  return api.del<AuftragLinkedAssetsResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/assets/${encodeURIComponent(String(assetId))}`,
  );
}

export function postOrderNote(
  orderId: number,
  body: { note: string; visibility: string; references: ProjectNoteReference[] },
): Promise<ProjectNote> {
  return api.post(`/auftraege/${encodeURIComponent(String(orderId))}/notes`, body);
}

export function deleteOrderNote(orderId: number, noteId: number | string): Promise<unknown> {
  return api.del(`/auftraege/${encodeURIComponent(String(orderId))}/notes/${encodeURIComponent(String(noteId))}`);
}

export function resetProjectCompletion(orderId: number): Promise<unknown> {
  return api.post(`/auftraege/${encodeURIComponent(String(orderId))}/project-completion/reset`);
}

export interface LateHourPermission {
  id: number;
  auftrag_id: number;
  user_login: string;
  user_display?: string | null;
  granted_by_login: string;
  granted_by_display?: string | null;
  granted_at?: string | null;
}

export function listLateHourPermissions(orderId: number): Promise<LateHourPermission[]> {
  return api.get(`/auftraege/${encodeURIComponent(String(orderId))}/late-hour-permissions`);
}

export function grantLateHourPermission(
  orderId: number,
  payload: { user_login: string; user_display?: string | null },
): Promise<LateHourPermission> {
  return api.post(`/auftraege/${encodeURIComponent(String(orderId))}/late-hour-permissions`, payload);
}

export function revokeLateHourPermission(orderId: number, userLogin: string): Promise<unknown> {
  return api.del(
    `/auftraege/${encodeURIComponent(String(orderId))}/late-hour-permissions/${encodeURIComponent(userLogin)}`,
  );
}

export function updateOrderHeroCustomFields(
  orderId: number,
  fields: Array<{ schemaId: string; propertyUuid: string; value: string }>,
): Promise<unknown> {
  return api.put(`/auftraege/${encodeURIComponent(String(orderId))}/hero-custom-fields`, {
    hero_custom_fields: fields,
  });
}

export function updateOrder(orderId: number, payload: UpdateOrderPayload): Promise<Order> {
  return api.put(`/auftraege/${encodeURIComponent(String(orderId))}`, payload);
}

export function patchOrderAnsprechpartner(
  orderId: number,
  ansprechpartnerId: number,
): Promise<Order> {
  return api.patch(`/auftraege/${encodeURIComponent(String(orderId))}/ansprechpartner`, {
    ansprechpartner_id: ansprechpartnerId,
  });
}

export function patchOrderReferenzFields(
  orderId: number,
  payload: {
    bestellnummer?: string | null;
    kundenreferenz?: string | null;
    leitweg_id?: string | null;
  },
): Promise<Order> {
  return api.patch(`/auftraege/${encodeURIComponent(String(orderId))}/referenz-fields`, payload);
}

export function searchEmployees(
  q: string,
  limit = 50,
): Promise<
  Array<{
    id: string;
    label?: string;
    subtitle?: string;
    name?: string;
    email?: string;
    is_nachunternehmer?: boolean;
  }>
> {
  return api.get(`/employees/search${buildQuery({ q, limit })}`);
}

export function searchOrders(
  q: string,
  limit = 8,
): Promise<
  Array<{
    id: number;
    auftragsnummer?: string;
    kunde?: string;
    beschreibung?: string;
    jahr?: number;
    ort?: string;
  }>
> {
  return api.get(`/auftraege/search${buildQuery({ q, limit })}`);
}

export function searchOrderReferences(
  orderId: number,
  kind: "orders" | "timesheets",
  q: string,
  limit = 8,
): Promise<Array<{ id: number | string; label?: string; display?: string; subtitle?: string }>> {
  return api.get(
    `/auftraege/${encodeURIComponent(String(orderId))}/reference-search${buildQuery({ kind, q, limit })}`,
  );
}

export type MontageunterlageEntryType = "file" | "note" | "address";
export type DocumentPack = "montage" | "werkstatt" | "aufmass";

export interface Montageunterlage {
  id: number;
  auftrag_id: number;
  entry_type: MontageunterlageEntryType;
  pack?: DocumentPack | string;
  title?: string | null;
  body_text?: string | null;
  maps_url?: string | null;
  filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  sort_order?: number;
  created_by_login?: string | null;
  created_by_display?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  file_url?: string | null;
  sharepoint_web_url?: string | null;
  storage?: "sharepoint" | "database" | null;
}

export function isWerkstattPackItem(item: Pick<Montageunterlage, "pack">): boolean {
  return String(item.pack || "montage").toLowerCase() === "werkstatt";
}

export function isAufmassPackItem(item: Pick<Montageunterlage, "pack">): boolean {
  return String(item.pack || "").toLowerCase() === "aufmass";
}

export interface MontageunterlageSharePointFile {
  id: string;
  name: string;
  size?: number | null;
  webUrl?: string | null;
  already_linked?: boolean;
}

export interface MontageunterlageSharePointFilesResponse {
  items: MontageunterlageSharePointFile[];
  sharepoint_available: boolean;
  hint?: string | null;
}

export function getMontageunterlagen(orderId: number): Promise<Montageunterlage[]> {
  return api.get<Montageunterlage[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen`,
    { cache: "no-store" },
  );
}

export function getWerkstattunterlagen(orderId: number): Promise<Montageunterlage[]> {
  return api.get<Montageunterlage[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen`,
    { cache: "no-store" },
  );
}

export function getAufmassunterlagen(orderId: number): Promise<Montageunterlage[]> {
  return api.get<Montageunterlage[]>(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen`,
    { cache: "no-store" },
  );
}

export function getMontageAbOhnePreisePdfUrl(orderId: number): string {
  return `${API_BASE || ""}/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/auftragsbestaetigung/pdf`;
}

async function fetchMontagePdfBlob(url: string): Promise<Blob> {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `${res.status} ${res.statusText}`;
    try {
      const parsed = JSON.parse(text) as { detail?: string };
      if (parsed?.detail) message = parsed.detail;
    } catch {
      /* plain text */
    }
    throw new Error(message);
  }
  return res.blob();
}

export async function fetchMontageAbOhnePreisePdf(orderId: number): Promise<Blob> {
  return fetchMontagePdfBlob(getMontageAbOhnePreisePdfUrl(orderId));
}

export async function getMontageAbAvailability(
  orderId: number,
): Promise<{ exists: boolean; dokument_id?: number | null }> {
  return api.get(`/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/auftragsbestaetigung`);
}

export function getMontageDeckblattPdfUrl(orderId: number): string {
  return `${API_BASE || ""}/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/deckblatt/pdf`;
}

export function getWerkstattDeckblattPdfUrl(orderId: number): string {
  return `${API_BASE || ""}/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/deckblatt/pdf`;
}

export async function fetchMontageDeckblattPdf(orderId: number): Promise<Blob> {
  return fetchMontagePdfBlob(getMontageDeckblattPdfUrl(orderId));
}

export async function fetchWerkstattDeckblattPdf(orderId: number): Promise<Blob> {
  return fetchMontagePdfBlob(getWerkstattDeckblattPdfUrl(orderId));
}

export function getAufmassDeckblattPdfUrl(orderId: number): string {
  return `${API_BASE || ""}/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/deckblatt/pdf`;
}

export async function fetchAufmassDeckblattPdf(orderId: number): Promise<Blob> {
  return fetchMontagePdfBlob(getAufmassDeckblattPdfUrl(orderId));
}

export function linkMontageunterlagenFromProjectFotos(
  orderId: number,
  fotoIds: number[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/from-project-fotos`,
    { foto_ids: fotoIds },
  );
}

export function linkWerkstattunterlagenFromProjectFotos(
  orderId: number,
  fotoIds: number[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/from-project-fotos`,
    { foto_ids: fotoIds },
  );
}

export function linkAufmassunterlagenFromProjectFotos(
  orderId: number,
  fotoIds: number[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/from-project-fotos`,
    { foto_ids: fotoIds },
  );
}

export async function uploadMontageunterlageFile(
  orderId: number,
  file: File,
  title?: string,
): Promise<Montageunterlage> {
  const fd = new FormData();
  fd.append("file", file);
  if (title?.trim()) fd.append("title", title.trim());
  return api.postForm<Montageunterlage>(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/file`,
    fd,
  );
}

export async function uploadWerkstattunterlageFile(
  orderId: number,
  file: File,
  title?: string,
): Promise<Montageunterlage> {
  const fd = new FormData();
  fd.append("file", file);
  if (title?.trim()) fd.append("title", title.trim());
  return api.postForm<Montageunterlage>(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/file`,
    fd,
  );
}

export async function uploadAufmassunterlageFile(
  orderId: number,
  file: File,
  title?: string,
): Promise<Montageunterlage> {
  const fd = new FormData();
  fd.append("file", file);
  if (title?.trim()) fd.append("title", title.trim());
  return api.postForm<Montageunterlage>(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/file`,
    fd,
  );
}

export function createMontageunterlageNote(
  orderId: number,
  body: { body_text: string; title?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/note`,
    body,
  );
}

export function createWerkstattunterlageNote(
  orderId: number,
  body: { body_text: string; title?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/note`,
    body,
  );
}

export function createAufmassunterlageNote(
  orderId: number,
  body: { body_text: string; title?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/note`,
    body,
  );
}

export function getAuftragAppAngebotTextePdfUrl(orderId: number): string {
  return `${API_BASE}/auftraege/${encodeURIComponent(String(orderId))}/app-angebot/texte/pdf`;
}

export async function openAuftragAppAngebotTextePdf(orderId: number): Promise<void> {
  const url = getAuftragAppAngebotTextePdfUrl(orderId);
  const { openFileInDefaultApp, isElectronWithOpenTempFile } = await import("@/lib/electron");
  if (isElectronWithOpenTempFile()) {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    await openFileInDefaultApp(blob, `Angebotstexte-${orderId}.pdf`);
    return;
  }
  window.open(url, "_blank", "noopener");
}

export function createAppAngebotTexteMontageunterlage(orderId: number): Promise<Montageunterlage> {
  return api.post<Montageunterlage>(
    `/auftraege/${encodeURIComponent(String(orderId))}/app-angebot/texte/montageunterlage`,
  );
}

export function createMontageunterlageAddress(
  orderId: number,
  body: { body_text: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/address`,
    body,
  );
}

export function createWerkstattunterlageAddress(
  orderId: number,
  body: { body_text: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/address`,
    body,
  );
}

export function createAufmassunterlageAddress(
  orderId: number,
  body: { body_text: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/address`,
    body,
  );
}

export function updateMontageunterlage(
  orderId: number,
  itemId: number,
  body: { body_text?: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.put(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/${encodeURIComponent(String(itemId))}`,
    body,
  );
}

export function updateWerkstattunterlage(
  orderId: number,
  itemId: number,
  body: { body_text?: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.put(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/${encodeURIComponent(String(itemId))}`,
    body,
  );
}

export function updateAufmassunterlage(
  orderId: number,
  itemId: number,
  body: { body_text?: string; title?: string; maps_url?: string },
): Promise<Montageunterlage> {
  return api.put(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/${encodeURIComponent(String(itemId))}`,
    body,
  );
}

export function deleteMontageunterlage(orderId: number, itemId: number): Promise<unknown> {
  return api.del(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/${encodeURIComponent(String(itemId))}`,
  );
}

export function deleteWerkstattunterlage(orderId: number, itemId: number): Promise<unknown> {
  return api.del(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/${encodeURIComponent(String(itemId))}`,
  );
}

export function deleteAufmassunterlage(orderId: number, itemId: number): Promise<unknown> {
  return api.del(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/${encodeURIComponent(String(itemId))}`,
  );
}

export function getMontageunterlageFileUrl(orderId: number, itemId: number): string {
  const base = API_BASE || "";
  return `${base}/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/${encodeURIComponent(String(itemId))}/file`;
}

export function getWerkstattunterlageFileUrl(orderId: number, itemId: number): string {
  const base = API_BASE || "";
  return `${base}/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/${encodeURIComponent(String(itemId))}/file`;
}

export function getMontageunterlageSharePointFiles(
  orderId: number,
): Promise<MontageunterlageSharePointFilesResponse> {
  return api.get<MontageunterlageSharePointFilesResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/sharepoint-files`,
    { cache: "no-store" },
  );
}

export function getWerkstattunterlageSharePointFiles(
  orderId: number,
): Promise<MontageunterlageSharePointFilesResponse> {
  return api.get<MontageunterlageSharePointFilesResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/sharepoint-files`,
    { cache: "no-store" },
  );
}

export function getAufmassunterlageSharePointFiles(
  orderId: number,
): Promise<MontageunterlageSharePointFilesResponse> {
  return api.get<MontageunterlageSharePointFilesResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/sharepoint-files`,
    { cache: "no-store" },
  );
}

export function linkMontageunterlageSharePointFiles(
  orderId: number,
  sharepointItemIds: string[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/sharepoint-link`,
    { sharepoint_item_ids: sharepointItemIds },
  );
}

export function linkWerkstattunterlageSharePointFiles(
  orderId: number,
  sharepointItemIds: string[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/werkstattunterlagen/sharepoint-link`,
    { sharepoint_item_ids: sharepointItemIds },
  );
}

export function linkAufmassunterlageSharePointFiles(
  orderId: number,
  sharepointItemIds: string[],
): Promise<Montageunterlage[]> {
  return api.post(
    `/auftraege/${encodeURIComponent(String(orderId))}/aufmassunterlagen/sharepoint-link`,
    { sharepoint_item_ids: sharepointItemIds },
  );
}

export function resolveMontageunterlageFileUrl(item: Montageunterlage, orderId: number): string {
  return item.file_url || getMontageunterlageFileUrl(orderId, item.id);
}

export interface MailFilePreview {
  subject?: string | null;
  from_label?: string | null;
  to_label?: string | null;
  cc_label?: string | null;
  date?: string | null;
  body_html?: string | null;
  body_text?: string | null;
  attachments?: string[];
}

export function isMailMontageFile(item: {
  filename?: string | null;
  title?: string | null;
  content_type?: string | null;
}): boolean {
  const type = String(item.content_type || "").toLowerCase();
  const names = [item.filename, item.title].map((value) => String(value || "").trim().toLowerCase());
  return (
    names.some((name) => name.endsWith(".eml") || name.endsWith(".msg")) ||
    type.includes("rfc822") ||
    type.includes("ms-outlook")
  );
}

export function getMontageunterlageMailPreview(
  orderId: number,
  itemId: number,
): Promise<MailFilePreview> {
  return api.get<MailFilePreview>(
    `/auftraege/${encodeURIComponent(String(orderId))}/montageunterlagen/${encodeURIComponent(String(itemId))}/preview`,
    { cache: "no-store" },
  );
}

export async function fetchMontageunterlageFileBlob(
  orderId: number,
  item: Montageunterlage,
): Promise<Blob> {
  const url = resolveMontageunterlageFileUrl(item, orderId);
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function getTimesheet(timesheetId: number | string): Promise<{
  id?: number;
  display_number?: string;
  timesheet_number?: string | number;
  status?: string;
  date_from?: string;
  date_to?: string;
}> {
  return api.get(`/timesheets/${encodeURIComponent(String(timesheetId))}`, { cache: "no-store" });
}

/* ─────────────────────────── Projektfotos (Büro) ─────────────────────────── */

export interface ProjectFoto {
  id: number;
  upload_id: number;
  filename: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  sharepoint_web_url?: string | null;
  uploaded_by_display?: string | null;
  uploaded_at?: string | null;
  preview_url?: string | null;
  file_url?: string | null;
  is_image?: boolean;
  can_delete?: boolean;
}

const PROJECT_FOTO_IMAGE_EXT = /\.(png|jpe?g|webp|gif|bmp|heic|heif|tiff?)$/i;

/** Bild, auch wenn SharePoint nur die Endung und keinen Bild-MIME-Typ geliefert hat. */
export function projectFotoIsImage(
  foto: Pick<ProjectFoto, "is_image" | "content_type" | "filename" | "original_filename">,
): boolean {
  const names = [foto.filename, foto.original_filename];
  if (names.some((name) => PROJECT_FOTO_IMAGE_EXT.test((name || "").trim()))) return true;
  if (foto.is_image === false) return false;
  const contentType = (foto.content_type || "").toLowerCase();
  return foto.is_image === true || !contentType || contentType.startsWith("image/");
}

export function projectFotoFolderLabel(segment: string | null | undefined): string {
  const cleaned = (segment || "").trim();
  return cleaned ? `08 - Fotos/${cleaned}` : "08 - Fotos";
}

export interface ProjectFotoFolder {
  folder_segment: string;
  sharepoint_folder_id: string;
  title: string;
  session_id?: number | null;
  photo_count: number;
}

export interface ProjectFotoSession {
  id: number;
  title: string;
  folder_segment: string;
  created_by_display?: string | null;
  created_at?: string | null;
  photo_count: number;
  photos: ProjectFoto[];
}

export interface ProjectFotoMobileQrResponse {
  url?: string;
  qr_data_uri?: string;
  expires_at?: string | null;
}

export async function listProjectFotos(orderId: number): Promise<ProjectFotoSession[]> {
  const data = await api.get<{ sessions?: ProjectFotoSession[] }>(
    `/auftraege/${orderId}/fotos`,
    { cache: "no-store" },
  );
  return Array.isArray(data.sessions) ? data.sessions : [];
}

export async function listProjectFotoFolders(
  orderId: number,
): Promise<ProjectFotoFolder[]> {
  const data = await api.get<{ folders?: ProjectFotoFolder[] }>(
    `/auftraege/${orderId}/fotos/folders`,
    { cache: "no-store" },
  );
  return Array.isArray(data.folders) ? data.folders : [];
}

export async function openProjectFotoSession(
  orderId: number,
  payload: {
    sharepoint_folder_id: string;
    folder_segment: string;
    title?: string | null;
  },
): Promise<ProjectFotoSession> {
  return api.post<ProjectFotoSession>(
    `/auftraege/${orderId}/fotos/sessions/open`,
    payload,
  );
}

export async function createProjectFotoSession(
  orderId: number,
  title: string,
): Promise<ProjectFotoSession> {
  return api.post<ProjectFotoSession>(`/auftraege/${orderId}/fotos/sessions`, { title });
}

export async function getProjectFotoMobileUploadQr(
  orderId: number,
  sessionId: number,
): Promise<ProjectFotoMobileQrResponse> {
  return api.get<ProjectFotoMobileQrResponse>(
    `/auftraege/${orderId}/fotos/sessions/${sessionId}/mobile-upload-qr`,
  );
}

export async function uploadProjectFotoFile(
  orderId: number,
  sessionId: number,
  file: File,
): Promise<ProjectFoto> {
  const fd = new FormData();
  fd.append("file", file);
  return api.postForm<ProjectFoto>(
    `/auftraege/${orderId}/fotos/sessions/${sessionId}/files`,
    fd,
  );
}

export async function renameProjectFoto(
  orderId: number,
  fotoId: number,
  filename: string,
): Promise<ProjectFoto> {
  return api.patch<ProjectFoto>(`/auftraege/${orderId}/fotos/${fotoId}`, { filename });
}

export async function deleteProjectFoto(orderId: number, fotoId: number): Promise<void> {
  await api.del(`/auftraege/${orderId}/fotos/${fotoId}`);
}

export function getProjectFotoFileUrl(orderId: number, fotoId: number): string {
  const base = API_BASE || "";
  return `${base}/auftraege/${orderId}/fotos/${fotoId}/file`;
}

/** Anzeigegröße (JPEG), geeignet für die Großansicht auf dem Smartphone. */
export function getProjectFotoDisplayUrl(orderId: number, fotoId: number): string {
  return `${getProjectFotoFileUrl(orderId, fotoId)}?view=display`;
}

export function getProjectFotoThumbUrl(orderId: number, fotoId: number): string {
  const base = API_BASE || "";
  return `${base}/auftraege/${orderId}/fotos/${fotoId}/thumb`;
}

export function resolveProjectFotoPreviewUrl(
  orderId: number,
  foto: ProjectFoto,
): string {
  if (foto.preview_url?.startsWith("http")) return foto.preview_url;
  if (foto.is_image) return getProjectFotoThumbUrl(orderId, foto.id);
  return getProjectFotoFileUrl(orderId, foto.id);
}

export function resolveProjectFotoFullUrl(
  orderId: number,
  foto: ProjectFoto,
): string {
  if (foto.file_url?.startsWith("http")) return foto.file_url;
  return getProjectFotoFileUrl(orderId, foto.id);
}

export interface SharePointStandardFolderStatus {
  role: string;
  name: string;
  exists: boolean;
  matched_name?: string | null;
}

export interface SharePointStandardFoldersStatus {
  sharepoint_folder_id?: string | null;
  complete: boolean;
  folders: SharePointStandardFolderStatus[];
  missing: string[];
}

export function getSharePointStandardFolders(
  orderId: number,
): Promise<SharePointStandardFoldersStatus> {
  return api.get<SharePointStandardFoldersStatus>(
    `/auftraege/${encodeURIComponent(String(orderId))}/sharepoint-standard-folders`,
    { cache: "no-store" },
  );
}

export function createSharePointStandardFolders(
  orderId: number,
): Promise<SharePointStandardFoldersStatus> {
  return api.post<SharePointStandardFoldersStatus>(
    `/auftraege/${encodeURIComponent(String(orderId))}/sharepoint-standard-folders`,
    {},
  );
}

export interface DuplicateNummerStatus {
  eligible: boolean;
  shared_auftragsnummer?: string;
  keeper_auftrag_id?: number;
  keeper_auftragsnummer?: string;
  keeper_ordner_label?: string;
  target_jahr?: number;
  naechste_auftragsnummer?: string;
}

export interface DuplicateNummerRenumberResult {
  auftrag_id: number;
  alte_auftragsnummer: string;
  neue_auftragsnummer: string;
  keeper_auftrag_id: number;
  keeper_auftragsnummer: string;
}

export function getDuplicateNummerStatus(orderId: number): Promise<DuplicateNummerStatus> {
  return api.get<DuplicateNummerStatus>(
    `/auftraege/${encodeURIComponent(String(orderId))}/duplicate-nummer`,
    { cache: "no-store" },
  );
}

export function renumberDuplicateNummer(orderId: number): Promise<DuplicateNummerRenumberResult> {
  return api.post<DuplicateNummerRenumberResult>(
    `/auftraege/${encodeURIComponent(String(orderId))}/duplicate-nummer-renumber`,
    {},
  );
}
